import type { Resource, NetWorthSnapshotAttributes } from '../api/types';

/** Ranges the Dashboard's net-worth trend chip can show. Today only '1M' is
 *  wired up (the hero always passes range="1M"), but the type covers the
 *  others net-worth.ts's `changes` object already computes. */
export type TrendRange = '1M' | 'YTD' | '1Y';

function subtractDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function startOfYear(isoDate: string): string {
  return `${isoDate.slice(0, 4)}-01-01`;
}

/** The earliest date (inclusive) a trend range covers, anchored to the most
 *  recent snapshot's own date (not "today") — so a workspace whose last
 *  snapshot is stale still windows correctly relative to what it actually has. */
export function trendRangeStart(range: TrendRange, latestDate: string): string {
  switch (range) {
    case '1M':  return subtractDays(latestDate, 30);
    case '1Y':  return subtractDays(latestDate, 365);
    case 'YTD': return startOfYear(latestDate);
  }
}

/**
 * Windows a full net-worth history (ascending by date) down to just the span
 * the trend chip above it covers, returning the plain `netWorth` values ready
 * for the sparkline. Without this, a sparkline plotting the FULL history lets
 * old, no-longer-relevant swings (e.g. seed data from account setup) dominate
 * its min/max normalization — a genuinely flat recent trend renders as a
 * dramatic cliff even while the trend chip correctly reads "+0.0%".
 */
export function windowNetWorthHistory(
  history: Resource<NetWorthSnapshotAttributes>[],
  range: TrendRange,
): number[] {
  if (history.length === 0) return [];
  const latestDate = history[history.length - 1]!.attributes.date;
  const start = trendRangeStart(range, latestDate);
  return history.filter(s => s.attributes.date >= start).map(s => s.attributes.netWorth);
}
